/**
 * Platform Roles and Permissions
 * Based on Wasilah Platform Access Control (ops/docs/platform-access-control.md)
 *
 * Portal Terminology (WICHTIG!):
 * - Client Portal: Krankenhäuser/Kliniken (Käufer) → client-* Rollen
 * - Provider Portal: Lieferanten (Verkäufer) → provider-* Rollen
 * - Backoffice Portal: Wasilah Platform Admins → wasilah-* Rollen
 *
 * All access rules are defined centrally in Keycloak.
 * Companies can only assign predefined access levels - no custom roles.
 */

// =============================================================================
// User Types (Groups)
// =============================================================================

export type UserType = 'clients' | 'providers' | 'backoffice'

export const USER_TYPES = {
  CLIENTS: 'clients',
  PROVIDERS: 'providers',
  BACKOFFICE: 'backoffice',
} as const

// =============================================================================
// Access Levels (Composite Roles)
// =============================================================================

/** Client Roles (Krankenhäuser/Kliniken - Käufer) - Client Portal */
export const CLIENT_ROLES = {
  OWNER: 'client-owner',
  ADMIN: 'client-admin',
  USER: 'client-user',
  VIEWER: 'client-viewer',
} as const

/** Provider Roles (Lieferanten - Verkäufer) - Provider Portal */
export const PROVIDER_ROLES = {
  OWNER: 'provider-owner',
  ADMIN: 'provider-admin',
  USER: 'provider-user',
  VIEWER: 'provider-viewer',
} as const

/** Backoffice (Internal) Roles - wasilah-* prefix for platform admins */
export const BACKOFFICE_ROLES = {
  ADMIN: 'wasilah-admin',
  USER: 'wasilah-user',
  READER: 'wasilah-reader',
} as const

/** All composite roles */
export const ROLES = {
  // Client (Krankenhäuser/Kliniken - Käufer)
  CLIENT_OWNER: CLIENT_ROLES.OWNER,
  CLIENT_ADMIN: CLIENT_ROLES.ADMIN,
  CLIENT_USER: CLIENT_ROLES.USER,
  CLIENT_VIEWER: CLIENT_ROLES.VIEWER,
  // Provider (Lieferanten - Verkäufer)
  PROVIDER_OWNER: PROVIDER_ROLES.OWNER,
  PROVIDER_ADMIN: PROVIDER_ROLES.ADMIN,
  PROVIDER_USER: PROVIDER_ROLES.USER,
  PROVIDER_VIEWER: PROVIDER_ROLES.VIEWER,
  // Backoffice (Platform)
  Wasilah_ADMIN: BACKOFFICE_ROLES.ADMIN,
  Wasilah_USER: BACKOFFICE_ROLES.USER,
  Wasilah_READER: BACKOFFICE_ROLES.READER,
} as const

export type Role =
  | 'client-owner'
  | 'client-admin'
  | 'client-user'
  | 'client-viewer'
  | 'provider-owner'
  | 'provider-admin'
  | 'provider-user'
  | 'provider-viewer'
  | 'wasilah-admin'
  | 'wasilah-user'
  | 'wasilah-reader'

// =============================================================================
// Permissions (Fine-grained permissions)
// =============================================================================

export const PERMISSIONS = {
  // Requests/RFQs
  REQUESTS_VIEW: 'requests:view',
  REQUESTS_CREATE: 'requests:create',
  REQUESTS_EDIT: 'requests:edit',
  REQUESTS_DELETE: 'requests:delete',
  REQUESTS_APPROVE: 'requests:approve',

  // RFQ (Supplier side)
  RFQ_VIEW: 'rfq:view',
  RFQ_RESPOND: 'rfq:respond',

  // Quotes
  QUOTES_VIEW: 'quotes:view',
  QUOTES_CREATE: 'quotes:create',
  QUOTES_MANAGE: 'quotes:manage',

  // Orders
  ORDERS_VIEW: 'orders:view',
  ORDERS_CREATE: 'orders:create',
  ORDERS_MANAGE: 'orders:manage',
  ORDERS_APPROVE: 'orders:approve',

  // Organization
  ORG_USERS_VIEW: 'org:users:view',
  ORG_USERS_MANAGE: 'org:users:manage',
  ORG_SETTINGS: 'org:settings',

  // Analytics
  ANALYTICS: 'analytics:view',

  // Admin
  ADMIN_ALL: 'admin:all',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

// =============================================================================
// Role-Permission Mapping
// =============================================================================

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  // Client Roles (Krankenhäuser/Kliniken - Käufer) - Client Portal
  'client-owner': [
    PERMISSIONS.REQUESTS_VIEW,
    PERMISSIONS.REQUESTS_CREATE,
    PERMISSIONS.REQUESTS_EDIT,
    PERMISSIONS.REQUESTS_DELETE,
    PERMISSIONS.REQUESTS_APPROVE,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_MANAGE,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_CREATE,
    PERMISSIONS.ORDERS_MANAGE,
    PERMISSIONS.ORDERS_APPROVE,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ORG_USERS_MANAGE,
    PERMISSIONS.ORG_SETTINGS,
    PERMISSIONS.ANALYTICS,
  ],
  'client-admin': [
    PERMISSIONS.REQUESTS_VIEW,
    PERMISSIONS.REQUESTS_CREATE,
    PERMISSIONS.REQUESTS_EDIT,
    PERMISSIONS.REQUESTS_DELETE,
    PERMISSIONS.REQUESTS_APPROVE,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_MANAGE,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_CREATE,
    PERMISSIONS.ORDERS_MANAGE,
    PERMISSIONS.ORDERS_APPROVE,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ORG_USERS_MANAGE,
    PERMISSIONS.ORG_SETTINGS,
    PERMISSIONS.ANALYTICS,
  ],
  'client-user': [
    PERMISSIONS.REQUESTS_VIEW,
    PERMISSIONS.REQUESTS_CREATE,
    PERMISSIONS.REQUESTS_EDIT,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_CREATE,
  ],
  'client-viewer': [PERMISSIONS.REQUESTS_VIEW, PERMISSIONS.QUOTES_VIEW, PERMISSIONS.ORDERS_VIEW],

  // Provider Roles (Lieferanten - Verkäufer) - Provider Portal
  'provider-owner': [
    PERMISSIONS.RFQ_VIEW,
    PERMISSIONS.RFQ_RESPOND,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_CREATE,
    PERMISSIONS.QUOTES_MANAGE,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_MANAGE,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ORG_USERS_MANAGE,
    PERMISSIONS.ORG_SETTINGS,
    PERMISSIONS.ANALYTICS,
  ],
  'provider-admin': [
    PERMISSIONS.RFQ_VIEW,
    PERMISSIONS.RFQ_RESPOND,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_CREATE,
    PERMISSIONS.QUOTES_MANAGE,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_MANAGE,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ORG_USERS_MANAGE,
    PERMISSIONS.ORG_SETTINGS,
    PERMISSIONS.ANALYTICS,
  ],
  'provider-user': [
    PERMISSIONS.RFQ_VIEW,
    PERMISSIONS.RFQ_RESPOND,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_CREATE,
    PERMISSIONS.ORDERS_VIEW,
  ],
  'provider-viewer': [PERMISSIONS.RFQ_VIEW, PERMISSIONS.QUOTES_VIEW, PERMISSIONS.ORDERS_VIEW],

  // Backoffice Roles (Platform Admins) - Backoffice Portal
  'wasilah-admin': [
    PERMISSIONS.ADMIN_ALL,
    PERMISSIONS.REQUESTS_VIEW,
    PERMISSIONS.REQUESTS_CREATE,
    PERMISSIONS.REQUESTS_EDIT,
    PERMISSIONS.REQUESTS_DELETE,
    PERMISSIONS.REQUESTS_APPROVE,
    PERMISSIONS.RFQ_VIEW,
    PERMISSIONS.RFQ_RESPOND,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_CREATE,
    PERMISSIONS.QUOTES_MANAGE,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_CREATE,
    PERMISSIONS.ORDERS_MANAGE,
    PERMISSIONS.ORDERS_APPROVE,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ORG_USERS_MANAGE,
    PERMISSIONS.ORG_SETTINGS,
    PERMISSIONS.ANALYTICS,
  ],
  'wasilah-user': [
    PERMISSIONS.REQUESTS_VIEW,
    PERMISSIONS.RFQ_VIEW,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ANALYTICS,
  ],
  'wasilah-reader': [
    PERMISSIONS.REQUESTS_VIEW,
    PERMISSIONS.RFQ_VIEW,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORG_USERS_VIEW,
    PERMISSIONS.ANALYTICS,
  ],
}

/**
 * Check if a role has a specific permission
 */
export const roleHasPermission = (role: Role, permission: Permission): boolean => {
  const permissions = ROLE_PERMISSIONS[role]
  return permissions?.includes(permission) || permissions?.includes(PERMISSIONS.ADMIN_ALL)
}

/**
 * Get all permissions for a set of roles
 */
export const getPermissionsForRoles = (roles: Role[]): Permission[] => {
  const permissions = new Set<Permission>()
  for (const role of roles) {
    const rolePerms = ROLE_PERMISSIONS[role]
    if (rolePerms) {
      rolePerms.forEach(p => permissions.add(p))
    }
  }
  return Array.from(permissions)
}

/**
 * Check if user roles include a specific permission
 */
export const hasPermission = (userRoles: string[], permission: Permission): boolean => {
  for (const role of userRoles) {
    if (roleHasPermission(role as Role, permission)) {
      return true
    }
  }
  return false
}

/**
 * Check if user roles include any of the given permissions
 */
export const hasAnyPermission = (userRoles: string[], permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(userRoles, permission))
}

/**
 * Check if user roles include all of the given permissions
 */
export const hasAllPermissions = (userRoles: string[], permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(userRoles, permission))
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a user has a specific role
 */
export const hasRole = (userRoles: string[], role: Role): boolean => {
  return userRoles.includes(role)
}

/**
 * Check if user is a client (any client role) - Client Portal users (hospitals/clinics)
 */
export const isClient = (userRoles: string[]): boolean => {
  return Object.values(CLIENT_ROLES).some(role => userRoles.includes(role))
}

/**
 * Check if user is a provider (any provider role) - Provider Portal users (suppliers)
 */
export const isProvider = (userRoles: string[]): boolean => {
  return Object.values(PROVIDER_ROLES).some(role => userRoles.includes(role))
}

/**
 * Check if user is backoffice (any backoffice role)
 */
export const isBackoffice = (userRoles: string[]): boolean => {
  return Object.values(BACKOFFICE_ROLES).some(role => userRoles.includes(role))
}

/**
 * Check if user is any kind of admin
 */
export const isAdmin = (userRoles: string[]): boolean => {
  return (
    userRoles.includes(CLIENT_ROLES.ADMIN) ||
    userRoles.includes(PROVIDER_ROLES.ADMIN) ||
    userRoles.includes(BACKOFFICE_ROLES.ADMIN)
  )
}

/**
 * Check if user is platform admin
 */
export const isPlatformAdmin = (userRoles: string[]): boolean => {
  return userRoles.includes(BACKOFFICE_ROLES.ADMIN)
}

/**
 * Get user type from roles
 */
export const getUserType = (userRoles: string[]): UserType | null => {
  if (isBackoffice(userRoles)) return 'backoffice'
  if (isClient(userRoles)) return 'clients'
  if (isProvider(userRoles)) return 'providers'
  return null
}
